const comparisonRows = [
  { category: "Particle Capture", tpa: "Down to 0.0146 microns", hepa: "Only 0.3 microns" },
  { category: "Wildfire Smoke",   tpa: "Never clogs", hepa: "Clogs fast" },
  { category: "Pollutant Action", tpa: "Destroys viruses & VOCs", hepa: "Traps only" },
  { category: "Whisper Quiet",    tpa: "As low as 25dB", hepa: "Loud at high speeds" },
  { category: "Maintenance",      tpa: "Washable plates", hepa: "Swap every 6–12 mo" },
  { category: "Long-Term Cost",   tpa: "One-time investment", hepa: "Ongoing filter costs" },
  { category: "Waste",            tpa: "Zero filter waste", hepa: "Disposable landfill waste" },
];

export default function ComparisonSection() {

  return (
    <section id="products" className="min-h-[75vh] md:min-h-[80vh] flex flex-col justify-center bg-white py-6 md:py-8 md:pb-12 scroll-mt-20 overflow-hidden relative z-10">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl w-full">

        {/* Section header */}
        <div className="mb-4 md:mb-5">
          {/* Label row */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand/10 text-[11px] font-extrabold tracking-[0.15em] uppercase text-brand">
              <i className="ri-flashlight-line text-xs" />
              Performance
            </span>
          </div>
          
          {/* Main heading */}
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight mb-2 text-center">
            Airdog TPA vs. HEPA — <span className="text-brand">No Contest</span>
          </h2>
          
          {/* Key value prop — bold dark badge */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 bg-gray-900 text-white pl-2.5 pr-4 py-2 rounded-xl">
              <div className="w-7 h-7 rounded-lg bg-amber-400/20 flex items-center justify-center shrink-0">
                <i className="ri-trophy-line text-amber-400 text-sm" />
              </div>
              <span className="text-xs font-bold leading-snug">Airdog TPA outperforms standard HEPA in every category that matters.</span>
            </div>
          </div>
        </div>

        {/* Two-column body */}
        <div className="grid lg:grid-cols-2 gap-4 md:gap-5 items-start">

          {/* Left — Comparison Table */}
          <div>
            <div className="grid grid-cols-3 mb-1">
              <div className="col-span-1" />
              <div className="col-span-1 px-1 py-0.5 text-center">
                <div className="inline-flex items-center gap-1 bg-brand/10 rounded-md px-1.5 py-0.5">
                  <i className="ri-shield-check-line text-brand text-[9px]" />
                  <span className="text-[10px] font-bold text-brand">Airdog TPA</span>
                </div>
              </div>
              <div className="col-span-1 px-1 py-0.5 text-center">
                <div className="inline-flex items-center gap-1 bg-gray-100 rounded-md px-1.5 py-0.5">
                  <i className="ri-filter-line text-gray-500 text-[9px]" />
                  <span className="text-[10px] font-bold text-gray-600">HEPA</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-100 overflow-hidden">
              {comparisonRows.map((row, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-3 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/70"} ${i !== comparisonRows.length - 1 ? "border-b border-gray-100" : ""}`}
                >
                  <div className="col-span-1 px-3 py-2 flex items-center">
                    <span className="text-[10px] font-extrabold text-gray-800 uppercase tracking-wide leading-tight">{row.category}</span>
                  </div>
                  <div className="col-span-1 px-3 py-2 flex items-start gap-1 border-l border-gray-100">
                    <div className="w-3.5 h-3.5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-emerald-600 text-[7px]" />
                    </div>
                    <span className="text-[11px] text-gray-900 font-bold leading-snug">{row.tpa}</span>
                  </div>
                  <div className="col-span-1 px-3 py-2 flex items-start gap-1 border-l border-gray-100">
                    <div className="w-3.5 h-3.5 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-close-line text-red-500 text-[7px]" />
                    </div>
                    <span className="text-[11px] text-gray-600 leading-snug font-bold">{row.hepa}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Airdog TPA Key Advantages */}
          <div className="flex flex-col">

            {/* Airdog TPA Key Advantages */}
            <div className="bg-[#FAF8F5] rounded-xl border border-gray-100 p-4">
              <p className="text-[11px] font-extrabold tracking-[0.15em] text-gray-700 uppercase mb-3 text-center">Airdog TPA Key Advantages</p>
              <div className="space-y-3">
                {/* Ultra-Fine Capture */}
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                    <i className="ri-crosshair-2-line text-brand text-xs" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <p className="text-[11px] font-extrabold tracking-wider text-gray-900 uppercase">Ultra-Fine Capture</p>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/15 text-[7px] font-extrabold text-blue-600 uppercase tracking-wider whitespace-nowrap">
                        <i className="ri-shield-check-line text-[8px]" />
                        Class 11 Medical Device
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-700 leading-relaxed font-bold">Captures and destroys particles 20× smaller than HEPA — effective against wildfire smoke down to 0.0146 microns.</p>
                  </div>
                </div>

                {/* Destroys Pollutants */}
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                    <i className="ri-flashlight-line text-brand text-xs" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-extrabold tracking-wider text-gray-900 uppercase mb-0.5">Destroys Pollutants</p>
                    <p className="text-[10px] text-gray-700 leading-relaxed font-bold">Ionic wind inactivates viruses and bacteria. Never clogs, never loses suction — always at peak performance.</p>
                  </div>
                </div>

                {/* No Filter Replacements */}
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                    <i className="ri-recycle-line text-brand text-xs" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-extrabold tracking-wider text-gray-900 uppercase mb-0.5">Zero Filter Waste</p>
                    <p className="text-[10px] text-gray-700 leading-relaxed font-bold">Washable collection plates — no disposable filters to buy, replace, or throw away. Just rinse and reuse.</p>
                  </div>
                </div>

                {/* Independently Tested */}
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                    <i className="ri-shield-star-line text-brand text-xs" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-extrabold tracking-wider text-gray-900 uppercase mb-0.5">Independently Tested</p>
                    <p className="text-[10px] text-gray-700 leading-relaxed font-bold">Certified by NHS, FDA, SGS &amp; JIS — trusted by health agencies worldwide.</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}